import { Table as PaymentsTable } from './components/Table';
import { useMIPManageContext } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/MIPManageContext';
import { downloadFileFromBlob } from '@dealstack/frontend/shared/files/downloadFile';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	bulkUpdateMipPaymentData,
	downloadMipPayments,
	getMipPaymentsData,
	updateMipPaymentData,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Payments/Payments.api';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { Flex } from '@mantine/core';

export const Payments = () => {
	const mipManageContext = useMIPManageContext();

	const {
		data: paymentsData,
		isLoading: isPaymentsLoading,
		refetch: refetchPayments,
	} = useQuery({
		queryKey: ['mipPayments'],
		queryFn: () => getMipPaymentsData(),
	});

	const { mutateAsync: downloadPayments, isPending: isDownloading } = useMutation({
		mutationFn: (variables: Parameters<typeof downloadMipPayments>[0]) =>
			downloadMipPayments(variables),
	});

	const { mutate: updatePayment, isPending: isUpdating } = useMutation({
		mutationFn: (variables: Parameters<typeof updateMipPaymentData>[0]) =>
			updateMipPaymentData(variables),
		onSuccess: async () => {
			await refetchPayments();
		},
	});

	const { mutate: bulkUpdatePayment, isPending: isBulkUpdating } = useMutation({
		mutationFn: (variables: Parameters<typeof bulkUpdateMipPaymentData>[0]) =>
			bulkUpdateMipPaymentData(variables),
		onSuccess: async (response) => {
			await refetchPayments();
			showToastV2({
				message: `${response.payload?.count ?? ''} payments updated successfully`,
			});
		},
	});

	const onDownloadPayments = async () => {
		const payload = await downloadPayments();

		if (!payload) {
			return;
		}
		const fileName = `${mipManageContext.deal?.name ?? 'unknown'}-${
			mipManageContext.mipData?.entityName ?? ''
		}-payments.xlsx`;
		downloadFileFromBlob(payload, fileName);
	};

	return (
		<Flex pos="relative" direction="column" w="100%" h="100%">
			{(isPaymentsLoading || isUpdating || isBulkUpdating) && <InlineLoader />}

			<PaymentsTable
				payments={paymentsData?.payload?.payments}
				summary={paymentsData?.payload?.statistics}
				updatePayment={updatePayment}
				bulkUpdatePayment={bulkUpdatePayment}
				onDownloadPayments={onDownloadPayments}
				isDownloading={isDownloading}
				isUpdating={isUpdating || isBulkUpdating}
			/>
		</Flex>
	);
};
